import { OpenShift } from "@clipboard-health/contract-worker-app-bff";
import { Box } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { type BookabilityStatusItem } from "@src/appV2/OpenShifts/ShiftAction";
import { List, ListItem, ListItemText } from "@src/appV2/redesign/system/ui-components";
import { useHistory } from "react-router-dom";

import { SHIFT_DISCOVERY_WORKPLACE_PROFILE_MODAL_PATH } from "../../../ShiftDiscovery/paths";
import { useWorkplaceModalsDataContext } from "../../../ShiftDiscovery/useWorkplaceModalsDataContext";
import { WorkplaceHighlightPills } from "../../../Workplace/Highlights/Pills";
import { useWorkplaceHighlights } from "../../../Workplace/Highlights/useWorkplaceHighlights";
import { QuestionTag } from "../../../Workplace/Review/types";
import { type BlockingRequirementsContext } from "../../Bookability/Documents/types";
import { ShiftBottomSheetBookabilityCardsContainer } from "../../BottomSheet/BookabilityCardsContainer";
import { type ShiftType } from "../../constants";
import { type Window } from "../../Open/types";
import { type ModalWorkplaceData, type WindowRestriction } from "../../types";
import { ShiftWorkplaceListItem } from "../../WorkplaceListItem";
import { BundleShiftPay } from "../types";
import { ShiftInfoListItemContent } from "./ShiftInfoListItemContent";

interface ShiftBundleBottomSheetContentProps {
  shiftId: string;
  shiftType: ShiftType;
  shiftQualification: string;
  shiftStartIso: string;
  workplace: ModalWorkplaceData;
  bookabilityStatus: BookabilityStatusItem;
  blockingRequirementsContext?: BlockingRequirementsContext;
  hasHoursRestrictionConflict: boolean;
  shiftFailedLoading: boolean;
  hasMarkedInterest?: boolean;
  restrictedToWindow?: WindowRestriction;
  isIndependentContractorBadgesRequired?: boolean;
  window?: Window;
  sortedBundleShifts: OpenShift[];
  bundleShiftPays: Map<string, BundleShiftPay>;
}

export function ShiftBundleBottomSheetContent(props: ShiftBundleBottomSheetContentProps) {
  const {
    shiftId,
    shiftType,
    shiftQualification,
    shiftStartIso,
    workplace,
    bookabilityStatus,
    blockingRequirementsContext,
    hasHoursRestrictionConflict,
    shiftFailedLoading,
    hasMarkedInterest,
    restrictedToWindow,
    isIndependentContractorBadgesRequired,
    window,
    sortedBundleShifts,
    bundleShiftPays,
  } = props;

  const history = useHistory();
  const { generateModalPath } = useWorkplaceModalsDataContext();

  const workplaceTimezone = workplace.attributes.location.timezone;

  const { highlights, isLoading: isHighlightsLoading } = useWorkplaceHighlights({
    workplaceId: workplace.id,
    excludeList: [QuestionTag.PATIENTS_PER_NURSE],
  });

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
      <ShiftBottomSheetBookabilityCardsContainer
        shiftId={shiftId}
        shiftType={shiftType}
        shiftQualification={shiftQualification}
        shiftStartIso={shiftStartIso}
        workplace={workplace}
        bookabilityStatus={bookabilityStatus}
        blockingRequirementsContext={blockingRequirementsContext}
        hasHoursRestrictionConflict={hasHoursRestrictionConflict}
        shiftFailedLoading={shiftFailedLoading}
        hasMarkedInterest={hasMarkedInterest}
        restrictedToWindow={restrictedToWindow}
        isIndependentContractorBadgesRequired={isIndependentContractorBadgesRequired}
        window={window}
      />

      <List sx={{ paddingX: 6 }}>
        <ShiftWorkplaceListItem
          workplaceId={workplace.id}
          workplaceName={workplace.attributes.name}
          workplaceType={workplace.attributes.type ?? ""}
          workplaceLocation={workplace.attributes.location}
          onClick={() => {
            logEvent(APP_V2_USER_EVENTS.VIEWED_FACILITY_PROFILE, {
              workplaceId: workplace.id,
              trigger: "Shift Bundle Bottom Sheet",
            });

            history.push(
              generateModalPath(SHIFT_DISCOVERY_WORKPLACE_PROFILE_MODAL_PATH, {
                workplaceId: workplace.id,
              })
            );
          }}
        />

        <ListItem divider sx={{ paddingTop: 0 }}>
          <ListItemText
            disableTypography
            sx={{ paddingLeft: "2rem" }}
            primary={
              <WorkplaceHighlightPills
                highlights={highlights}
                isLoading={isHighlightsLoading}
                wrapperSx={(theme) => ({
                  marginTop: `calc(-1 * ${theme.spacing(3)})`,
                })}
              />
            }
          />
        </ListItem>

        {sortedBundleShifts.map((shift) => {
          const totalPayInMinorUnits = bundleShiftPays.get(shift.id)?.totalPayInMinorUnits;
          const isOrientationShift = shift.attributes.orientationShiftType === "single-shift";
          return (
            <ListItem key={shift.id}>
              <ShiftInfoListItemContent
                timeSlot={shift.attributes.timeSlot}
                startIso={shift.attributes.start}
                endIso={shift.attributes.end}
                payInMinorUnits={totalPayInMinorUnits}
                workplaceTimezone={workplaceTimezone}
                isOrientationShift={isOrientationShift}
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
}
